import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FaEllipsisV } from 'react-icons/fa';
import { useModuleAccess } from '../utils/moduleAccess';

const MENU_WIDTH = 190;

// Row-level actions dropdown ("kebab" menu), replacing rows of individual icon buttons.
// Rendered via a portal to document.body and positioned with fixed coordinates so it
// never gets clipped by a scrollable table ancestor (overflow-x: auto on the table wrapper).
// items: [{ label, icon, onClick, show=true, disabled=false, danger=false, variant, divider=false, module }]
// An item with `module` is left out while that licensed module is switched off.
export const ActionsMenu = ({ items = [], title = 'Actions' }) => {
  const [open, setOpen] = useState(false);
  const moduleOn = useModuleAccess();
  const [coords, setCoords] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const computeCoords = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const openLeft = window.innerWidth - rect.right < MENU_WIDTH + 10;
    setCoords({
      top: rect.bottom + 4,
      left: openLeft ? Math.max(8, rect.right - MENU_WIDTH) : rect.left,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    computeCoords();
  }, [open, computeCoords]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) setOpen(false);
    };
    const handleEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    const handleReposition = () => computeCoords();
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEsc);
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEsc);
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [open, computeCoords]);

  const visibleItems = items.filter((i) => i && i.show !== false && (!i.module || moduleOn(i.module)));
  if (visibleItems.length === 0) return null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="actions-menu-trigger"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        title={title}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <FaEllipsisV />
      </button>
      {open && coords && createPortal(
        <div
          ref={menuRef}
          className="actions-menu-dropdown actions-menu-portal"
          style={{ position: 'fixed', top: coords.top, left: coords.left, width: MENU_WIDTH }}
          onClick={(e) => e.stopPropagation()}
        >
          {visibleItems.map((item, idx) => (
            item.divider ? (
              <div key={idx} className="actions-menu-divider" />
            ) : (
              <button
                key={idx}
                type="button"
                className={`actions-menu-item ${item.danger ? 'actions-menu-item-danger' : item.variant ? `actions-menu-item-${item.variant}` : ''}`}
                disabled={item.disabled}
                title={item.description || undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  item.onClick && item.onClick(e);
                }}
              >
                {item.icon && <span className="actions-menu-item-icon">{item.icon}</span>}
                <span>{item.label}</span>
              </button>
            )
          ))}
        </div>,
        document.body
      )}
    </>
  );
};

export default ActionsMenu;
